import { useState } from "react";

interface ShareData {
  title: string;
  text?: string;
  url: string;
}

interface UseSharingReturn {
  shareNative: (data: ShareData) => Promise<void>;
  shareToFacebook: (url: string) => void;
  shareToTwitter: (text: string, url: string) => void;
  shareToLinkedIn: (url: string, title?: string) => void;
  copyToClipboard: (url: string) => Promise<boolean>;
  isNativeShareSupported: boolean;
  isSharing: boolean;
  shareError: string | null;
}

export const useSharing = (): UseSharingReturn => {
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  const isNativeShareSupported = typeof navigator !== "undefined" && "share" in navigator;

  const shareNative = async (data: ShareData): Promise<void> => {
    if (!isNativeShareSupported) {
      throw new Error("Native sharing not supported");
    }

    setIsSharing(true);
    setShareError(null);

    try {
      await navigator.share({
        title: data.title,
        text: data.text,
        url: data.url,
      });
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        setShareError("Failed to share");
        throw error;
      }
    } finally {
      setIsSharing(false);
    }
  };

  const shareToFacebook = (url: string): void => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(facebookUrl, "_blank", "width=600,height=400");
  };

  const shareToTwitter = (text: string, url: string): void => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(
      url
    )}`;
    window.open(twitterUrl, "_blank", "width=600,height=400");
  };

  const shareToLinkedIn = (url: string, title?: string): void => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}${
      title ? `&title=${encodeURIComponent(title)}` : ""
    }`;
    window.open(linkedInUrl, "_blank", "width=600,height=400");
  };

  const copyToClipboard = async (url: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        return true;
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = url;
        textArea.style.position = "absolute";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          document.body.removeChild(textArea);
          return successful;
        } catch (err) {
          document.body.removeChild(textArea);
          return false;
        }
      }
    } catch (err) {
      return false;
    }
  };

  return {
    shareNative,
    shareToFacebook,
    shareToTwitter,
    shareToLinkedIn,
    copyToClipboard,
    isNativeShareSupported,
    isSharing,
    shareError,
  };
};
